import { useContext, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { useClerk, useUser } from '@clerk/clerk-react';

import { canUseMePortal } from '@/components/me/mePortal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { AccessContext, type AccessContextValue } from '@/contexts/AccessContext';

/**
 * Reason the session cannot use the web admin.
 *
 * - `noActiveOrganization`: the session carries no active organization, so the
 *   role is UNKNOWABLE. This is not the same state and must not reuse the
 *   employee copy — a real admin lands here when Clerk has not activated an
 *   org for the session (issue #1255, adversarial gap A2).
 * - `noWebAccess`: an employee whose professional role holds zero web modules
 *   (issue #1670). The role may still hold the employee app or the /me
 *   portal; the card then links there.
 */
export type StaffNotAuthorizedReason = 'noActiveOrganization' | 'noWebAccess';

interface StaffNotAuthorizedPageProps {
  reason: StaffNotAuthorizedReason;
}

/**
 * What the access response says is missing. `/access/me` answers 403 without
 * a reason when no contract covers today, the contract has no role or the
 * role has no module, so that case gets the sentence naming all three.
 */
type MissingAccess = 'unknown' | 'noContract' | 'noWebModule';

function missingAccess(access: AccessContextValue | undefined): MissingAccess {
  if (!access || access.isAdmin || access.isLoading || access.isError) return 'unknown';
  return access.role === '' ? 'noContract' : 'noWebModule';
}

/**
 * StaffNotAuthorizedPage is the web-admin lock-out for a session that is not an
 * admin (issue #1255).
 *
 * It renders a single centred card with no sidebar, no navigation and no data
 * fetching of its own: it reads the access the root gate already resolved,
 * names what is missing in one sentence the employee can forward, and shows
 * the signed-in email. This page is UX only:
 * the backend RequireAdmin gate matrix is the security boundary, and this page
 * is not a substitute for it.
 */
export function StaffNotAuthorizedPage({ reason }: StaffNotAuthorizedPageProps) {
  const { i18n } = useLingui();
  const { user } = useUser();
  const { signOut } = useClerk();
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Undefined above the AccessProvider (the no-organisation branch).
  const access = useContext(AccessContext);
  const missing = missingAccess(access);
  const showPortalLink =
    reason === 'noWebAccess' && !!access && !access.isAdmin && !access.isError && canUseMePortal(access);

  // Move focus to the heading on mount so a screen reader announces the state
  // instead of leaving focus on a page that no longer exists.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const email =
    user?.primaryEmailAddress?.emailAddress ??
    user?.emailAddresses?.[0]?.emailAddress ??
    null;

  const handleSignOut = () => {
    void signOut();
  };

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          {/* A real h1 (not CardTitle's div) so screen readers announce the state.
              tabIndex={-1} makes it programmatically focusable but NOT tabbable, so
              suppressing the default outline costs no keyboard affordance — it only
              stops the mount-time focus from painting a box around the wrapped title. */}
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-2xl font-semibold leading-tight tracking-tight outline-none"
          >
            {reason === 'noActiveOrganization' ? (
              <Trans id="staffNotAuthorized.noActiveOrganization.title">
                No organisation selected
              </Trans>
            ) : (
              <Trans id="staffNotAuthorized.noWebAccess.title">No web access</Trans>
            )}
          </h1>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            {reason === 'noActiveOrganization' ? (
              <Trans id="staffNotAuthorized.noActiveOrganization.message">
                This session has no active organisation, so we cannot tell which
                organisation to open. Select an organisation and sign in again.
              </Trans>
            ) : missing === 'noWebModule' ? (
              <Trans id="staffNotAuthorized.noWebAccess.message">
                Your role has no web access. Ask your administrator.
              </Trans>
            ) : missing === 'noContract' ? (
              i18n._({
                id: 'staffNotAuthorized.noWebAccess.noContract',
                message: 'No work contract covers today. Ask your administrator.',
              })
            ) : (
              i18n._({
                id: 'staffNotAuthorized.noWebAccess.unknown',
                message:
                  'No work contract with a role covers today, or that role has no modules. Ask your administrator.',
              })
            )}
          </p>

          {email && (
            <p className="text-sm text-muted-foreground" data-testid="signed-in-email">
              <Trans id="staffNotAuthorized.signedInAs">Signed in as</Trans>{' '}
              <span className="font-medium">{email}</span>
            </p>
          )}

          {showPortalLink && (
            <Button asChild className="w-full">
              <Link to="/me">
                {i18n._({ id: 'staffNotAuthorized.openPortal', message: 'Open my account' })}
              </Link>
            </Button>
          )}

          <Button variant="outline" className="w-full" onClick={handleSignOut}>
            <Trans id="staffNotAuthorized.signOut">Sign out</Trans>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default StaffNotAuthorizedPage;
